import { useMemo, useRef, ReactNode, useState } from 'react';
import * as React from 'react';
import Row from '@sdk/components/ui/spacing/Row';
import Column from '@sdk/components/ui/spacing/Column';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import gettext from '@sdk/lang/gettext';
import { OrgSettings } from '@sdk/authentication/types/OrganisationType';
import Text14 from '@sdk/components/ui/typography/Text14';
import Text16 from '@sdk/components/ui/typography/Text16';
import ProgressButton from '@sdk/components/ui/buttons/ProgressButton';
import { ButtonType } from '@sdk/components/ui/buttons/ButtonType';
import { SUBSCRIPTION_LEVELS } from '@sdk/account/constants';
import SlingTip from '@sdk/common/slingTip/SlingTip';
import { CurrencyType } from '@sdk/common/currency/types/CurrencyType';

import UpgradeSubscriptionModal from '@web/account/billing/UpgradeSubscriptionModal/UpgradeSubscriptionModal';
import { useBillingInfo } from '@web/account/billing/BillingInfoContext';
import { SubscriptionUtilsType } from '@web/account/types';
import CheckmarkBlue from '@web/assets/svg/UI/checkmark-blue-small.svg';
import { YearlyBilling } from '@web/account/components/YearlyBilling';

import { getSlingFeatures } from '../utils';
import { useBillingPlans } from '../hooks/billingPlans.hooks';

import {
  BillingSectionColumn,
  CheckmarkWithText,
  TableCell,
  Table,
  TrialButtonWrapper,
  TrialStartRow,
} from './billingPlans.styles';
import BillingPlanActions from './BillingPlanActions';
import BillingPlanStatus from './BillingPlanStatus';

// TODO it should be moved to react/account/billing/BillingPlansBanner/BillingPlansBanner.tsx
const getTipContent = (
  subscriptionLevel: number,
  isOnTrial: boolean,
  trialDaysLeft: number,
  isToastBilling: boolean,
  planName: string
): ReactNode => {
  if (isToastBilling) {
    if (isOnTrial) {
      return `Your ${planName} trial will expire in ${trialDaysLeft} days, at which point paid features will be inaccessible`;
    }

    if (subscriptionLevel) {
      return gettext('You are currently subscribed to %(planName)s.', { planName });
    }

    return (
      <>
        {gettext('You are currently subscribed to %(planName)s.', { planName })}
        <br />
        {gettext('Upgrade your subscription to Scheduling Pro to gain access to more features!')}
      </>
    );
  }

  if (subscriptionLevel < 2) {
    return (
      <>
        {gettext('You are using the %(planName)s plan of Sling.', { planName })}
        <br />
        {gettext('Upgrade your subscription to gain access to more features!')}
      </>
    );
  }

  if (!isOnTrial) {
    return <>{gettext('You are using the %(planName)s plan of Sling.', { planName })}</>;
  }
};

export type Props = {
  callbackAfterUpgrade?: () => void;
  orgSettings: OrgSettings;
  currency: CurrencyType;
  showPricing: boolean;
  startTrial: () => Promise<void>;
  goToPurchase: (plan: number, isAnnual: boolean) => void;
  goToBilling: () => void;
  subscriptionUtils: SubscriptionUtilsType;
};

const BillingPlans: React.FC<React.PropsWithChildren<Props>> = ({
  callbackAfterUpgrade,
  orgSettings,
  startTrial,
  goToPurchase,
  currency,
  subscriptionUtils,
  showPricing,
  goToBilling,
}) => {
  const isOldPremium = orgSettings.isOldPremium;
  const updateSubscriptionModalRef = useRef(null);
  const { billingInfo } = useBillingInfo();
  const {
    subscriptionLevel,
    isPremium,
    isOnTrial,
    isToastBilling,
    trialDaysLeft,
    canStartTrial,
    planName,
  } = billingInfo;
  const [isPlanAnnual, togglePlan] = useState(false);

  const billingPlans = useBillingPlans(
    orgSettings,
    subscriptionLevel,
    isOnTrial,
    isToastBilling,
    isPlanAnnual
  );
  const slingFeatures = useMemo(
    () => getSlingFeatures(orgSettings, isOldPremium || isToastBilling),
    [orgSettings, isOldPremium, isToastBilling]
  );

  const isSubscriptionCanceled = !!billingInfo.subscriptionCancelDate;
  const isSubscriptionCancellationPeriod = isSubscriptionCanceled && isPremium;

  const tipConent = useMemo(() => {
    return (
      !isSubscriptionCanceled &&
      getTipContent(subscriptionLevel, isOnTrial, trialDaysLeft, isToastBilling, planName)
    );
  }, [
    subscriptionLevel,
    isOnTrial,
    trialDaysLeft,
    isToastBilling,
    isSubscriptionCanceled,
    planName,
  ]);

  let levelsToDisplay = [
    SUBSCRIPTION_LEVELS.FREE,
    SUBSCRIPTION_LEVELS.PREMIUM,
    SUBSCRIPTION_LEVELS.BUSINESS,
  ];
  if (isOldPremium) {
    levelsToDisplay = [SUBSCRIPTION_LEVELS.FREE, SUBSCRIPTION_LEVELS.OLD_PREMIUM];
  } else if (isToastBilling) {
    levelsToDisplay = [SUBSCRIPTION_LEVELS.FREE, SUBSCRIPTION_LEVELS.BUSINESS];
  }

  const isOnHighestLevel =
    !isOnTrial &&
    subscriptionLevel >=
      (isOldPremium ? SUBSCRIPTION_LEVELS.OLD_PREMIUM : SUBSCRIPTION_LEVELS.BUSINESS);

  const _startTrial = async () => {
    await startTrial();

    callbackAfterUpgrade();
  };

  const isAlreadySubscribed = showPricing && isPremium;

  const tip =
    tipConent && !showPricing ? (
      <Row mTop={-20}>
        <SlingTip isVisible={true}>
          <Text14 semiBold themeColor={ThemeColorType.white}>
            {tipConent}
          </Text14>
        </SlingTip>
      </Row>
    ) : null;

  return (
    <>
      {canStartTrial && !isToastBilling ? ( // TODO this should be moved to BillingPlansBanner
        <TrialStartRow alignItems="center" pTop={20} pBottom={20} mBottom={20}>
          <Column>
            <Text14 semiBold>{gettext('You are eligible for a free Business trial!')}</Text14>
          </Column>
          <TrialButtonWrapper>
            <ProgressButton buttonType={ButtonType.primary} onClick={_startTrial}>
              {gettext('Start')} {orgSettings.premiumTrialLength}
              {gettext('-day trial now')}
            </ProgressButton>
          </TrialButtonWrapper>
        </TrialStartRow>
      ) : canStartTrial && isToastBilling ? (
        <TrialStartRow alignItems="center" pTop={20} pBottom={20} mBottom={20}>
          <Column>
            <Text14 semiBold>{gettext('You are eligible for a free Scheduling Pro trial!')}</Text14>
          </Column>
          <TrialButtonWrapper>
            <ProgressButton buttonType={ButtonType.primary} onClick={_startTrial}>
              {gettext('Start')} {orgSettings.premiumTrialLength}
              {gettext('-day trial now')}
            </ProgressButton>
          </TrialButtonWrapper>
        </TrialStartRow>
      ) : (
        tip
      )}
      <Table>
        <tbody>
          {!isToastBilling && !isOldPremium && (
            <tr>
              <YearlyBilling isPlanAnnual={isPlanAnnual} togglePlan={togglePlan} />
            </tr>
          )}
          <tr>
            {levelsToDisplay.map((level, levelIndex) => (
              <TableCell key={level} style={{ width: 100 / levelsToDisplay.length + '%' }}>
                <BillingPlanStatus
                  plan={billingPlans.status[level]}
                  showStatusLabel={
                    levelIndex === levelsToDisplay.length - 1 &&
                    (!isOldPremium || (subscriptionLevel && !isOnTrial))
                  }
                  isCurrentPlan={subscriptionLevel === level && !isOnTrial}
                  isPlanAnnual={isPlanAnnual}
                  level={level}
                  goToPurchase={goToPurchase}
                  billingPlans={billingPlans}
                  isOnHighestLevel={isOnHighestLevel}
                  upgradeSubscriptionLevel={() =>
                    updateSubscriptionModalRef.current.open(SUBSCRIPTION_LEVELS.BUSINESS)
                  }
                  isAlreadySubscribed={isAlreadySubscribed}
                  isSubscriptionCancellationPeriod={isSubscriptionCancellationPeriod}
                  isToastBilling={isToastBilling}
                />
              </TableCell>
            ))}
          </tr>
          {billingPlans.features.map((group) => (
            <tr key={group.title}>
              {levelsToDisplay.map((level, index) => (
                <TableCell key={level} style={{ width: 100 / levelsToDisplay.length + '%' }}>
                  <BillingSectionColumn>
                    <Text16 semiBold themeColor={ThemeColorType.gray}>
                      {index === 0 ? group.title : '\xa0'}
                    </Text16>
                    <Column mLeft={20} mTop={20}>
                      {group.list
                        .filter((item) => item in slingFeatures && slingFeatures[item] <= level)
                        .map((item, index) => (
                          <CheckmarkWithText key={item} mTop={index ? 15 : 0}>
                            <CheckmarkBlue />
                            <Text14 left={10} themeColor={ThemeColorType.gray}>
                              {gettext(item)}
                            </Text14>
                          </CheckmarkWithText>
                        ))}
                    </Column>
                  </BillingSectionColumn>
                </TableCell>
              ))}
            </tr>
          ))}
          {(!isOnHighestLevel && (
            <tr>
              {levelsToDisplay.map((level) => (
                <TableCell key={level} style={{ width: 100 / levelsToDisplay.length + '%' }} middle>
                  <Column pTop={20} pLeft={40} pRight={40} pBottom={40}>
                    <BillingPlanActions
                      level={level}
                      isPlanAnnual={isPlanAnnual}
                      goToPurchase={goToPurchase}
                      billingPlans={billingPlans}
                      center={true}
                      upgradeSubscriptionLevel={() =>
                        updateSubscriptionModalRef.current.open(SUBSCRIPTION_LEVELS.BUSINESS)
                      }
                      isAlreadySubscribed={showPricing && isPremium}
                      isSubscriptionCancellationPeriod={isSubscriptionCancellationPeriod}
                      isToastBilling={isToastBilling}
                    />
                  </Column>
                </TableCell>
              ))}
            </tr>
          )) ||
            null}
        </tbody>
      </Table>
      <UpgradeSubscriptionModal
        ref={updateSubscriptionModalRef}
        currency={currency}
        orgSettings={orgSettings}
        subscriptionUtils={subscriptionUtils}
        onSuccessSubmitCallback={isAlreadySubscribed ? goToBilling : null}
      />
    </>
  );
};

export default BillingPlans;
